import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import Chart from "react-apexcharts";
import { setPastDueSTOCheck } from "../../../../store/Actions";
import {
  formatToKOrMWithoutDecimal,
  getFormatedDate,
} from "../../../../helper";
import { formatToKOrM } from "../../../../helper";
import ScrollBarForGraph from "../ScrollBarForGraph.js/index.js";

const BarChart = ({
  // chartData = [],
  // categoryList = [],
  showAnnotation = false,
  yAxisTitle = "Values",
  graphData = [],
  activeTab = "Recommendation",
  type,
}) => {
  const [borderRadius, setBorderRadius] = useState(10);
  // const [isStacked, setIsStacked] = useState(false);
  const isStacked = false;
  const dispatch = useDispatch();
  const itemsPerPage = 7;
  const [currentPage, setCurrentPage] = useState(0);
  const totalItems = graphData?.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = currentPage * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;

  useEffect(() => {
    const handleResize = () => {
      const screenWidth = window.innerWidth;
      if (screenWidth < 1000) {
        setBorderRadius(5);
      } else if (screenWidth < 1500) {
        setBorderRadius(6);
      } else {
        setBorderRadius(10);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // Ensure graphData is an array
  const safeGraphData = Array.isArray(graphData) ? graphData : [];

  // Calculate column width based on number of bars
  const calculateColumnWidth = () => {
    const categoriesLength = safeGraphData.length;

    if (categoriesLength === 1) {
      return "2%";
    } else if (categoriesLength === 2) {
      return "5%";
    } else if (categoriesLength === 3) {
      return "8%";
    } else if (categoriesLength === 4 || categoriesLength === 5) {
      return "10%";
    } else if (categoriesLength === 6 || categoriesLength === 7) {
      return "12%";
    } else {
      return "15%";
    }
  };

  // Generate series data based on activeTab
  const seriesData = safeGraphData?.map((item) => {
    switch (activeTab) {
      case "COF":
        return item?.COF || 0;
      case "Cost Reductions":
        return item?.actual_cost_saving || 0;
      case "Cost per Case":
        return item?.Cost_Per_Case || 0;
      case "ST Utilization":
        return item?.avg_st_utilization || 0;
      case "LT Utilization":
        return item?.avg_lt_utilization || 0;
      case "Waste Risk":
        return item?.QuantityValue_dollars || 0;
      case "Inv. On Hand":
        return (
          (type === "Inv.On Hand Dollars"
            ? item?.avg_inventory_on_hand_dollars
            : item?.AVG_DOH_Stock_Coverage_Days) || 0
        );
      case "Throughput":
        return item?.value || 0;
      default:
        return item?.Recommended_diverts || 0;
    }
  });

  // Generate x-axis categories
  const xAxisCategories = safeGraphData?.map((item) => {
    if (activeTab === "Waste Risk" || activeTab === "Inv. On Hand") {
      return item?.Metric_name || "NA";
    } else if (
      activeTab === "ST Utilization" ||
      activeTab === "LT Utilization"
    ) {
      if (type === "Network") {
        return item?.Metric_name || "NA";
      } else {
        return item?.DC_Name || "NA";
      }
    } else if (activeTab === "Throughput") {
      return item?.DC_Name || "NA";
    } else if (type === "DIP" && activeTab === "COF") {
      return item?.X_Axis || "NA";
    } else {
      return item?.Network || "Unknown Network"; // Default to 'Unknown Network' if Network is falsy
    }
  });

  // Slice data for current page
  const slicedCategories = xAxisCategories?.slice(startIndex, endIndex);
  const slicedSeries = seriesData?.slice(startIndex, endIndex);

  const options = {
    chart: {
      type: "bar",
      height: 350,
      stacked: true, // Set this flag for stacked bars
      toolbar: {
        show: false,
      },
      zoom: {
        enabled: true,
        type: "x",
        autoScaleYaxis: true,
      },
      events: {
        dataPointSelection: (_event, _chartContext, config) => {
          const clickedBarIndex = config.dataPointIndex;
          const clickedBarData = safeGraphData[clickedBarIndex];
          const apiData = {
            selectedOption: clickedBarData,
            PastDueFlag: true,
            tab: activeTab,
          };
          if (
            activeTab === "ST Utilization" ||
            activeTab === "LT Utilization" ||
            activeTab === "STRs Required"
          ) {
            dispatch(setPastDueSTOCheck(apiData));
          }
        },
      },
    },
    fill: {
      colors: ["#EC6FBD"],
    },
    series: [
      {
        name: yAxisTitle,
        data: slicedSeries,
      },
    ],
    yaxis: {
      labels: {
        formatter: function (value) {
          const roundedValue = Math.round(value * 10) / 10;
          if (
            activeTab === "COF" ||
            activeTab === "ST Utilization" ||
            activeTab === "LT Utilization"
          ) {
            return `${roundedValue}%`;
          } else if (activeTab === "Throughput" && type === "%") {
            return roundedValue?.toFixed(0);
          } else if (type === "Inv.On Hand DOH" || type === "Pallets") {
            return formatToKOrM(roundedValue);
          } else if (type === "DIP" || type === "Inv.On Hand Dollars") {
            return `$${formatToKOrM(roundedValue)}`;
          } else {
            return `${roundedValue}`;
          }
          // return(
          //   activeTab === "COF"
          //   || activeTab === "ST Utilization"
          //   || activeTab === "LT Utilization"
          // ) ? `${roundedValue}%`
          //   : activeTab === "Throughput" && type === "%" ? value?.toFixed(4)
          //   :(type === "DIP" || type === "Inv.On Hand Dollars" || type === "Inv.On Hand DOH" || type === "Pallets")
          //       ? type === "Inv.On Hand DOH" || type === "Pallets" ? formatToKOrM(roundedValue) : `$${formatToKOrM(roundedValue)}`
          //       : `${roundedValue}`;
        },
      },
      title: {
        text:
          yAxisTitle === "Utilization" || yAxisTitle === "Fulfillment"
            ? yAxisTitle + " %"
            : yAxisTitle,
        style: {
          fontSize: "12px",
          fontFamily: "Poppins, sans-serif",
          fontWeight: 500,
          color: "#333",
        },
      },
      min: activeTab === "COF" ? 0 : undefined,
      max: activeTab === "COF" ? 100 : undefined,
      tickAmount: activeTab === "COF" ? 5 : undefined,
    },
    xaxis: {
      categories:
        xAxisCategories?.length > 0 ? slicedCategories : ["No Data Available"],
      axisBorder: {
        offsetY: activeTab === "COF" ? 3 : 0,
      },
    },
    tooltip: {
      shared: true,
      intersect: false,
      y: {
        formatter: (val, { _seriesIndex, dataPointIndex }) => {
          let avgUtil = 0;
          if (
            activeTab === "ST Utilization" ||
            activeTab === "LT Utilization"
          ) {
            if (activeTab === "ST Utilization") {
              avgUtil = graphData[dataPointIndex]?.avg_st_utilization;
            } else if (activeTab === "LT Utilization") {
              avgUtil = graphData[dataPointIndex]?.avg_lt_utilization;
            }
            let pallets = formatToKOrMWithoutDecimal(
              graphData[dataPointIndex]?.Pallets,
            );
            return `Avg Utilization: ${avgUtil?.toFixed(0)}% &nbsp &nbsp Pallets: ${pallets}`;
          } else if (activeTab === "COF") {
            return `${val?.toFixed(2)}%`;
          } else if (activeTab === "Throughput" && type === "%") {
            return `${val?.toFixed(0)}%`;
          } else if (type === "Inv.On Hand DOH" || type === "Pallets") {
            return formatToKOrM(val);
          } else if (type === "DIP" || type === "Inv.On Hand Dollars") {
            return `$${formatToKOrM(val)}`;
          } else if (
            activeTab === "Cost per Case" ||
            activeTab === "Cost Reductions"
          ) {
            return `$${formatToKOrM(val)}`;
          } else {
            return `${val}`;
          }
        },
      },
    },
    annotations: {
      yaxis: showAnnotation
        ? [
            {
              y: type === "DOM" ? 95 : type === "DIP" ? 98 : 0.45,
              borderColor: "#B0B0B0",
              strokeDashArray: [5, 5],
              label: {
                borderColor: "#B0B0B0",
                style: {
                  color: "#B0B0B0",
                  padding: "20px",
                  fontSize: "10px",
                },
                text:
                  type === "DOM"
                    ? "Target = 95%"
                    : type === "DIP"
                      ? "Target = 98%"
                      : "Target = $0.45",
              },
            },
          ]
        : [],
    },
    grid: {
      show: false,
    },
    dataLabels: {
      enabled: false,
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: calculateColumnWidth(),
        borderRadius: isStacked ? 0 : borderRadius, // Conditionally apply borderRadius based on stacking
        borderColors: ["#000000"],
        borderWidth: 2,
      },
    },
    noData: {
      text: "No Data Available",
      align: "center",
      verticalAlign: "middle",
      style: {
        color: "#404041",
        fontSize: "14px",
        fontFamily: "Poppins, sans-serif",
      },
    },
  };

  return (
    <div id="chart" style={{ padding: "10px 20px" }}>
      <Chart
        options={options}
        series={options.series}
        type="bar"
        height={300}
      />
      <ScrollBarForGraph
        totalPages={totalPages}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
      />
    </div>
  );
};

export default BarChart;
